import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import App from './App';
import DemoApp from './DemoApp';
import LoginPage from './pages/LoginPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import './index.css';

// BTv2.3 — Side-Effect: registriert window.__btv2DebugFeatures(symbol, atIdx)
// für den Konsole-Smoke-Test des Bar-by-Bar-Adapters.

// Globale Query-Client-Konfiguration: passt zum DataState-Wrapper aus § 5.
// Defaults sind bewusst konservativ — einzelne Queries können sie überschreiben.
// W88 P5 (2026-05-27) — als export verfügbar gemacht damit Batch-Prefetch
// (lib/market-data/batch-prefetch.ts) Cache populieren kann (60→1 Roundtrip).
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Loading-Timeout: 12 s. Danach wirft TanStack Query, useDataState fängt den Fehler.
      // (Implementiert in src/hooks/use-data-state.ts via AbortController.)
      staleTime: 5 * 60 * 1000, // 5 Min — danach 'stale', UI zeigt Hinweis
      gcTime: 30 * 60 * 1000, // 30 Min — Cache-Lebensdauer
      retry: (failureCount, error) => {
        // Bei 4xx (Client-Fehler) nicht retryen; bei 5xx max. 2-mal.
        const status = (error as { status?: number })?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Demo-/Review-Version unter /demo — keine privaten Daten, kein Login */}
          <Route path="/demo/*" element={<DemoApp />} />
          {/* Lab-Bereich — öffentlich, kein Login (während Erstellungsphase) */}
          {/* Login-Seite — öffentlich erreichbar */}
          <Route path="/login" element={<LoginPage />} />
          {/* Private Haupt-App — nur mit Session erreichbar */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <App />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools buttonPosition="bottom-right" />}
    </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
